import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import boardCertifiedImg from '../../images/star.png';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { formatYesNo } from '../../utils/yesNoDisplay';
import HoverTooltip from '../hoverTooltip/hoverTooltip';
import GroupAffiliationsModal from '../groupAffiliationsModal/groupAffiliationsModal';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const renderSpecialties = (providerSpecialty) => {
  if (!providerSpecialty || providerSpecialty.length === 0) {
    return { certified: <span>N/A</span>, notCertified: <span>N/A</span>, all: [] };
  }

  const certifiedSpecs = [];
  const notCertifiedSpecs = [];
  const allSpecs = [];

  providerSpecialty.forEach((spec, index) => {
    const element = (
      <span key={`${spec.specialtyName}-${index}`} className="specialty-item">
        {spec.specialtyName}
      </span>
    );

    if (spec.boardCertifiedFlag === 'Yes') {
      certifiedSpecs.push(element);
      allSpecs.push({
        name: spec.specialtyName,
        certified: true,
        index: index
      });
    } else {
      notCertifiedSpecs.push(element);
      allSpecs.push({
        name: spec.specialtyName,
        certified: false,
        index: index
      });
    }
  });

  // Add commas between spans
  const addCommas = (arr) => {
    return arr.reduce((acc, curr, idx) => {
      acc.push(curr);
      if (idx < arr.length - 1) {
        acc.push(<span key={`comma-${idx}`}>, </span>);
      }
      return acc;
    }, []);
  };

  return {
    certified: certifiedSpecs.length > 0 ? addCommas(certifiedSpecs) : <span>N/A</span>,
    notCertified: notCertifiedSpecs.length > 0 ? addCommas(notCertifiedSpecs) : <span>N/A</span>,
    all: allSpecs
  };
};

// Helper function to convert string to Title Case (Camel Case)
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// provider.accreditations entries come back from the real API shaped as
// { environmentId, provId, accreditingOrgName, accreditingOrgUrl } (same shape
// as the sibling provider.hospitals array used for the "Quality" section below).
// Accept a plain string OR an object under any of the plausible name/url keys,
// and otherwise drop the entry rather than ever rendering "[object Object]".
// Deliberately NOT run through toTitleCase (unlike affiliation names below)
// since accrediting-body names are often acronyms (e.g. "URAC", "DNV", "CIHQ")
// that title-casing would mangle.
const getAccreditationEntry = (accreditation) => {
  if (!accreditation) return null;
  if (typeof accreditation === 'string') {
    return accreditation ? { name: accreditation, url: null } : null;
  }
  const name =
    accreditation.accreditingOrgName ||
    accreditation.accreditationName ||
    accreditation.accreditingBody ||
    accreditation.accreditationBody ||
    accreditation.name ||
    null;
  if (!name) return null;
  const url = accreditation.accreditingOrgUrl || accreditation.url || null;
  return { name, url };
};

// The "Quality" section previously parsed each provider.hospitals entry's own
// accreditingOrgName/accreditingOrgUrl (mirroring Accreditation's per-entry
// links). Per request, this is now a single common, static link shown
// whenever the provider has at least one hospitals entry — no per-entry
// name/URL parsing needed anymore.
const HOSPITAL_QUALITY_LINK = 'https://www.medicare.gov/hospitalcompare/search.html';

const ProviderDetailsType2 = ({
  provider,
  providerSpecialty,
  isExpanded = true
}) => {
  const { isProviderDetailsFieldEnabled } = usePlanFeatures();
  // Accreditation/Quality tooltips (and the modal's own focus handling below) should
  // only be reachable by keyboard while this "Provider details" accordion section is
  // actually expanded — see providerDetails.jsx's matching comment for why the shared
  // .expandable-content accordion (max-height:0/overflow:hidden, not display:none)
  // otherwise leaves collapsed content focusable.
  const tooltipTabIndex = isExpanded ? 0 : -1;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  // "Show all special skills" popup — only used when there are more than 3 special
  // skills to display (see the Special skills render below), mirroring the same
  // treatment used for group/hospital affiliations in providerDetails.jsx.
  const [showAllSpecialSkillsModal, setShowAllSpecialSkillsModal] = useState(false);
  // "You are now leaving..." confirmation modal for the Quality section's
  // Hospital Quality Data external link — same pattern/component used for
  // browseBy.jsx's external Quick Links (e.g. Pharmacies).
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleHospitalQualityLinkClick = (e) => {
    e.preventDefault();
    setPendingExternalUrl(HOSPITAL_QUALITY_LINK);
    setExternalLinkModalOpen(true);
  };

  // Accreditation entries link out to each accrediting org's own external site
  // (accreditingOrgUrl) — same "you are now leaving" confirmation as the
  // Quality section's link above, just with a per-entry URL instead of the
  // single common HOSPITAL_QUALITY_LINK.
  const handleAccreditationLinkClick = (e, url) => {
    e.preventDefault();
    setPendingExternalUrl(url);
    setExternalLinkModalOpen(true);
  };

  const handleExternalLinkConfirm = () => {
    if (pendingExternalUrl) {
      window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
    }
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };

  const handleExternalLinkCancel = () => {
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };

  // Restore focus to the "Check specialties" button once its modal closes.
  useModalFocusReturn(isModalOpen);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close (see hook doc for why plain overflow:hidden
  // alone caused both of those symptoms).
  useBodyScrollLock(isModalOpen);

  // Move focus into the modal on open (its close button), trap Tab within it while
  // open, and close on Escape. This modal previously had none of these — unlike the
  // dedicated <Modal>-style components (e.g. languageModal.jsx) — which is why focus
  // never visibly moved anywhere when it opened. Declared after useModalFocusReturn
  // so that hook captures the real outside trigger before this one steals focus.
  useEffect(() => {
    if (!isModalOpen) return undefined;

    closeButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  const accreditationEntries = (provider?.accreditations || [])
    .map(getAccreditationEntry)
    .filter(Boolean);

  const specialties = renderSpecialties(providerSpecialty);
  
  return (
    <div className="frameDetail">
            {/* Check Specialties Link - Always visible at top */}
            {specialties.all.length > 0 ? (
              <div className="check-specialties-wrapper">
                <button 
                  onClick={openModal}
                  className="check-specialties-link"
                  aria-label="Check all specialties"
                >
                  <span>Check specialties</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="chevron-icon">
                    <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="check-specialties-wrapper">
                <span className="no-specialties-found"><strong>Specialties</strong>: None found</span>
              </div>
            )}

            <div className="card-section-divider" role="separator" aria-hidden="true" />

            <div className="div-2">
                {/* Board certified and non-certified specialty sections removed for Type 2
                    providers ("Check specialties" button above covers that instead). This
                    used to leave behind an empty, invisible .div-3/.tool-tip/.div-7 spacer
                    node here — since it was still a flex child of .div-2 (gap: 16px), it
                    added an extra 16px band of blank space above "Accreditation" on top of
                    the .card-section-divider's own 20px of vertical margin, making the gap
                    between "Check specialties" and "Accreditation" look too large. Removed
                    entirely since it rendered nothing. */}
                <div className="group">
                    <div className="div-8">
                        {isProviderDetailsFieldEnabled('accreditation') && (
                          <div className="div-9">
                            <div className="label">
                                <div className="text-wrapper-2">Accreditation</div>

                                <HoverTooltip
                                  heading="Accreditation"
                                  body="<span>Expert health groups have checked this location. They say it meets quality standards for patient care and safety. You can find out if certain groups <a href='https://www.jointcommission.org/en/about-us/recognizing-excellence/find-accredited-international-organizations' target='_blank' rel='noopener noreferrer'>accredit a location</a>.</span>"
                                  className="tool-tip-and-icon"
                                  tabIndex={tooltipTabIndex}
                                />
                            </div>

                            <p className="text-wrapper-3">
                                {accreditationEntries.length > 0 ? (
                                  accreditationEntries.map((entry, idx) => (
                                    <React.Fragment key={`${entry.name}-${idx}`}>
                                      {idx > 0 && ', '}
                                      {entry.url ? (
                                        <a
                                          href={entry.url}
                                          onClick={(e) => handleAccreditationLinkClick(e, entry.url)}
                                          className="accreditation-link"
                                        >
                                          {entry.name}
                                        </a>
                                      ) : (
                                        entry.name
                                      )}
                                    </React.Fragment>
                                  ))
                                ) : 'No accreditation found'}
                            </p>
                          </div>
                        )}

                        {isProviderDetailsFieldEnabled('quality') && (
                          <div className="div-9">
                            <div className="label">
                                <div className="text-wrapper-2">Quality</div>

                                <HoverTooltip
                                  heading="Quality"
                                  body="Expert groups check and rate hospitals to make sure they give safe, quality care. This link can help you find quality hospitals."
                                  className="tool-tip-and-icon-2"
                                  tabIndex={tooltipTabIndex}
                                />
                            </div>

                            <p className="text-wrapper-3">
                                <a
                                  href={HOSPITAL_QUALITY_LINK}
                                  onClick={handleHospitalQualityLinkClick}
                                  className="accreditation-link"
                                >
                                  Hospital Quality Data
                                </a>
                            </p>
                          </div>
                        )}
                    </div>

                </div>

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                {isProviderDetailsFieldEnabled('specialTraining') && (
                    <div className="group-wrapper">
                        <div className="group-2">
                            <p className="special-training">
                                <span className="text-wrapper">Special skills:</span>

                                <span className="span">
                                    {' '}
                                    {(() => {
                                        const specialSkillNames = typeof provider?.specialTraining === 'string' && provider.specialTraining.trim().length > 0
                                            ? provider.specialTraining.split(',').map(s => s.trim()).filter(Boolean)
                                            : [];

                                        if (specialSkillNames.length === 0) {
                                            return 'None found';
                                        }

                                        if (specialSkillNames.length > 3) {
                                            return (
                                                <>
                                                    {specialSkillNames.slice(0, 3).join(', ')}{', '}
                                                    <button
                                                        type="button"
                                                        className="show-all-affiliations-link"
                                                        onClick={() => setShowAllSpecialSkillsModal(true)}
                                                        tabIndex={tooltipTabIndex}
                                                    >
                                                        Show all special skills
                                                    </button>
                                                    <GroupAffiliationsModal
                                                        isOpen={showAllSpecialSkillsModal}
                                                        onClose={() => setShowAllSpecialSkillsModal(false)}
                                                        affiliations={specialSkillNames}
                                                        title="Special skills"
                                                    />
                                                </>
                                            );
                                        }

                                        return specialSkillNames.join(', ');
                                    })()}
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                {isProviderDetailsFieldEnabled('cshcsProvider') && (
                    <div className="group-wrapper cshcs-wrapper">
                        <div className="group-2">
                            <p className="cshcs-provider">
                                <span className="text-wrapper">Children&rsquo;s special health care services (CSHCS) provider:</span>

                                <span className="span">
                                    {' '}
                                    {formatYesNo(provider?.cshcsProvider)}
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                <div className="div-10">
                    {isProviderDetailsFieldEnabled('provId') && (
                      <p className="p">
                          <span className="text-wrapper">Provider ID: </span>

                          <span className="span"  translate='no'>{(provider?.provId && provider.provId !== '') || (provider?.providerId && provider.providerId !== '') ? (provider.provId || provider.providerId) : 'None found'}</span>
                      </p>
                    )}

                    {isProviderDetailsFieldEnabled('npi') && (
                      <p className="p">
                          <span className="text-wrapper">NPI ID: </span>

                          <span className="span"  translate='no'>{provider?.npi && provider.npi !== '' ? provider.npi : 'None found'}</span>
                      </p>
                    )}
                    {isProviderDetailsFieldEnabled('stateLicenseNumber') && (
                      <p className="p">
                          <span className="text-wrapper">State license number:</span>

                          <span className="span"  translate='no'>{(provider?.stateLicenseNumber && provider.stateLicenseNumber !== '') ? (provider.stateLicenseNumber) : 'None found'}</span>
                      </p>
                    )}

                </div>
            </div>
            
            {/* Specialties Modal — portaled to document.body so it escapes the
                .results-list ancestor's `transform: translateZ(0)` (an Edge/Chromium
                repaint-bug workaround — see .results-list in results/styles.css),
                which otherwise becomes this `position: fixed` overlay's containing
                block instead of the viewport. That made the modal render centered
                within the (very tall) results list rather than the actual screen —
                i.e. appearing "in the middle of the results card section". */}
            {isModalOpen && ReactDOM.createPortal(
              <div className="modal-overlay" onClick={closeModal} role="presentation">
                <div
                  className="modal-content"
                  ref={modalRef}
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={`specialties-modal-title-${provider?.provId || provider?.providerId || 'type2'}`}
                >
                  <div className="modal-header">
                    <h2 id={`specialties-modal-title-${provider?.provId || provider?.providerId || 'type2'}`} className="modal-title">Specialties</h2>
                    <button 
                      ref={closeButtonRef}
                      className="modal-close-button" 
                      onClick={closeModal}
                      aria-label="Close modal"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="modal-body">
                    {/* All Specialties Combined */}
                    <div className="modal-section">
                      <div className="modal-specialties-combined">
                        {specialties.all.length > 0 ? (
                          <ul className="specialties-list">
                            {specialties.all.map((spec, idx) => (
                              <li key={`specialty-${idx}`} className="specialty-list-item">
                                {spec.certified && (
                                  <img className="specialty-star-icon" src={boardCertifiedImg} alt="Board certified" />
                                )}
                                <span className={spec.certified ? 'specialty-name certified' : 'specialty-name'}>
                                  {spec.name}
                                </span>
                                {spec.certified && (
                                  <span className="certified-badge">
                                    Board Certified
                                    <HoverTooltip
                                      heading="Board certified"
                                      body="<span>This provider passed special tests in their field of health care. You can find out if they are <a href='https://www.certificationmatters.org/find-my-doctor/' target='_blank' rel='noopener noreferrer'>board-certified</a> in a specialty. Learn more about <a href='https://www.certificationmatters.org/' target='_blank' rel='noopener noreferrer'>board certification</a>.</span>"
                                    />
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="no-specialties">No specialties available</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Rendered via portal to document.body — this component tree sits inside
                the results list, whose <ul> is intentionally promoted to its own
                compositor layer (transform: translateZ(0), see results/styles.css)
                to work around an Edge/Chromium paint bug. Per the CSS spec, a
                position:fixed element positions itself relative to the nearest
                transformed ancestor rather than the viewport — so without this
                portal, the modal's fixed overlay would render mispositioned/clipped
                inside that list instead of as a proper fullscreen overlay. */}
            {externalLinkModalOpen && ReactDOM.createPortal(
              <ExternalLinkModal
                isOpen={externalLinkModalOpen}
                onConfirm={handleExternalLinkConfirm}
                onCancel={handleExternalLinkCancel}
                url={pendingExternalUrl}
              />,
              document.body
            )}
        </div>
  );
};

export default ProviderDetailsType2;
